import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet, Router } from '@angular/router';
import { AuthenticationService } from './core/services/auth.service';
import { environment } from '../environments/environment';
import { Auth, authState } from '@angular/fire/auth';
import { Firestore, doc, onSnapshot, Unsubscribe } from '@angular/fire/firestore';
import { Subject, takeUntil } from 'rxjs';
import { ConsentBannerComponent } from './shared/components/consent-banner/consent-banner.component';
import { AnalyticsService } from './core/services/analytics.service';
import { AnalyticsContextService } from './core/services/analytics-context.service';
import { registerCallableReporter } from './core/services/telemetry';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, ConsentBannerComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  /** Element id of the injected support-widget loader, so it is inserted once. */
  private static readonly SUPPORT_SCRIPT_ID = 'echo-support-widget-js';

  private destroy$ = new Subject<void>();
  private userStatusUnsubscribe?: Unsubscribe;
  private supportTrackingRetries = 0;

  constructor(
    private authService: AuthenticationService,
    private auth: Auth,
    private firestore: Firestore,
    private analytics: AnalyticsService,
    private analyticsContext: AnalyticsContextService,
    private router: Router
  ) { }

  ngOnInit() {
    this.registerBackendErrorReporting();
    this.initSupportWidget();
    this.subscribeToAuthState();
  }

  /**
   * Route Firebase SDK failures into analytics.
   *
   * ErrorInterceptor covers HttpClient, which Echo barely uses; callables and
   * Firestore listeners are how it actually talks to the backend, and they
   * were reporting nothing. Registered here rather than injected so the
   * wrapper in telemetry.ts can stay a plain function usable from field
   * initialisers.
   */
  private registerBackendErrorReporting() {
    registerCallableReporter(failure => {
      this.analyticsContext.get().then(context => {
        this.analytics.logEvent('error_monitoring', {
          action: 'backend_error',
          callable_name: failure.name,
          error_code: failure.code,
          error_message: failure.message,
          duration_ms: failure.durationMs,
          current_route: this.router.url,
          ...context
        });
      });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up user status listener
    if (this.userStatusUnsubscribe) {
      this.userStatusUnsubscribe();
    }
  }

  /**
   * Load the Echo chat widget used for Solenex support.
   *
   * This replaces the 3CX live-chat selector. Echo now supports its own
   * customers through the same widget those customers embed on their own
   * sites, so support arrives on the product being supported.
   *
   * Loaded on every deployed environment. Localhost is skipped: the widget
   * checks the embedding page's hostname against the widget's allowed
   * origins, so it would only warn in the console.
   */
  private initSupportWidget() {
    try {
      const hostname = window.location.hostname || '';
      const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0';
      if (isLocalhost) {
        return;
      }

      const support = (environment as any).support;
      if (!support?.embedUrl || !support?.widgetId) {
        return;
      }

      // Prevent double-insert across re-bootstraps.
      if (document.getElementById(AppComponent.SUPPORT_SCRIPT_ID)) {
        return;
      }

      const script = document.createElement('script');
      script.id = AppComponent.SUPPORT_SCRIPT_ID;
      script.src = support.embedUrl;
      script.async = true;
      script.setAttribute('data-echo-widget', support.widgetId);
      document.body.appendChild(script);
    } catch (e) {
      // A support widget is never worth breaking the app over.
      // eslint-disable-next-line no-console
      console.warn('Failed to load the Echo support widget', e);
    }
  }

  private subscribeToAuthState() {
    // Subscribe to authentication state changes
    authState(this.auth)
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        if (user) {
          // Set analytics user ID and properties.
          //
          // Deliberately no email or display name: Google prohibits personally
          // identifiable information in Analytics and can delete a property's
          // data over it. Organisation and role are both more useful for
          // segmenting a B2B product anyway, and identify no one.
          this.analytics.setUserId(user.uid);
          this.analyticsContext.invalidate();
          this.analyticsContext.get().then(context => {
            this.analytics.setUserProperties({
              organization_id: context.organization_id,
              user_role: context.user_role,
              org_type: context.org_type
            });
          });

          // Monitor user's disabled status in real-time
          this.setupUserStatusListener(user.uid);

          // The support widget identifies the visitor itself once they start a
          // conversation, so nothing has to be injected into it here.
          this.setupSupportClickTracking();
        } else {
          // User logged out - clear analytics user ID
          this.analytics.setUserId(null);
          this.analyticsContext.invalidate();

          // Clean up user status listener
          if (this.userStatusUnsubscribe) {
            this.userStatusUnsubscribe();
            this.userStatusUnsubscribe = undefined;
          }
        }
      });
  }

  /**
   * Listen for changes to the user's disabled status
   * Signs out immediately if the user is disabled by an admin
   */
  private setupUserStatusListener(userId: string): void {
    // Clean up any existing listener
    if (this.userStatusUnsubscribe) {
      this.userStatusUnsubscribe();
    }

    // Listen to user document for disabled status changes
    const userDocRef = doc(this.firestore, `users/${userId}`);
    this.userStatusUnsubscribe = onSnapshot(userDocRef, async (snapshot) => {
      if (snapshot.exists()) {
        const userData = snapshot.data();
        if (userData?.['disabled'] === true) {
          // User has been disabled - sign them out immediately
          await Swal.fire({
            icon: 'warning',
            title: 'Account Disabled',
            text: 'Your account has been disabled by an administrator. You will be signed out.',
            confirmButtonColor: '#556ee6',
            allowOutsideClick: false,
            allowEscapeKey: false
          });

          // Sign out the user and redirect to login
          await this.authService.logout();
          await this.router.navigate(['/auth/login']);
        }
      }
    }, (error) => {
      console.error('Error listening to user status:', error);
    });
  }

  /**
   * Track the support widget being opened.
   *
   * The launcher lives inside the widget's own container, so the click is
   * caught on the container rather than on a button we own.
   */
  private setupSupportClickTracking() {
    try {
      const container = document.querySelector('[data-echo-chat]');
      if (!container) {
        // The widget renders only after its config request resolves.
        this.supportTrackingRetries += 1;
        if (this.supportTrackingRetries <= 10) {
          setTimeout(() => this.setupSupportClickTracking(), 2000);
        }
        return;
      }

      container.addEventListener('click', () => {
        this.analytics.logEvent('support_requested', {
          support_type: 'echo_chat',
          source: 'chat_bubble'
        });
      });
    } catch (e) {
      console.warn('Failed to set up support widget click tracking', e);
    }
  }
}
